import { apiRequest } from '../http';
import { IHumanResourcesRepository } from './IHumanResourcesRepository';
import {
  TemporalCompany,
  CreateTemporalCompanyInput,
  UpdateTemporalCompanyInput,
  Position,
  CreatePositionInput,
  UpdatePositionInput,
  Employee,
  CreateEmployeeInput,
  UpdateEmployeeInput,
  ShiftRecord,
  CreateShiftRecordInput,
  UpdateShiftRecordInput,
  ShiftRecordFilters,
  PagedResult,
  RateFactor,
  CreateRateFactorInput,
  UpdateRateFactorInput,
  CalendarDay,
  CreateCalendarDayInput,
  UpdateCalendarDayInput,
  ListCalendarDaysFilters,
  CalendarStagingRow,
  CreateStagingBatchInput,
  UpdateStagingRowInput,
  ListStagingFilters,
  SummaryRow,
} from './types';

export class RemoteHumanResourcesRepository implements IHumanResourcesRepository {
  constructor(private getToken: () => Promise<string | null>) {}

  async listTemporalCompanies(includeInactive = false): Promise<TemporalCompany[]> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/temporal-companies?includeInactive=${includeInactive}`, { token });
  }
  async createTemporalCompany(input: CreateTemporalCompanyInput): Promise<TemporalCompany> {
    const token = await this.getToken();
    return apiRequest('/api/hr/temporal-companies', { method: 'POST', body: input, token });
  }
  async updateTemporalCompany(id: string, patch: UpdateTemporalCompanyInput): Promise<TemporalCompany> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/temporal-companies/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteTemporalCompany(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/hr/temporal-companies/${id}`, { method: 'DELETE', token });
  }

  async listPositions(includeInactive = false): Promise<Position[]> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/positions?includeInactive=${includeInactive}`, { token });
  }
  async createPosition(input: CreatePositionInput): Promise<Position> {
    const token = await this.getToken();
    return apiRequest('/api/hr/positions', { method: 'POST', body: input, token });
  }
  async updatePosition(id: string, patch: UpdatePositionInput): Promise<Position> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/positions/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deletePosition(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/hr/positions/${id}`, { method: 'DELETE', token });
  }

  async listEmployees(includeInactive = false): Promise<Employee[]> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/employees?includeInactive=${includeInactive}`, { token });
  }
  async createEmployee(input: CreateEmployeeInput): Promise<Employee> {
    const token = await this.getToken();
    return apiRequest('/api/hr/employees', { method: 'POST', body: input, token });
  }
  async updateEmployee(id: string, patch: UpdateEmployeeInput): Promise<Employee> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/employees/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteEmployee(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/hr/employees/${id}`, { method: 'DELETE', token });
  }

  async listShiftRecords(filters: ShiftRecordFilters): Promise<PagedResult<ShiftRecord>> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.employeeId) params.set('employeeId', filters.employeeId);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    params.set('page', String(filters.page ?? 1));
    params.set('pageSize', String(filters.pageSize ?? 50));
    return apiRequest(`/api/hr/shift-records?${params.toString()}`, { token });
  }
  async createShiftRecord(input: CreateShiftRecordInput): Promise<ShiftRecord> {
    const token = await this.getToken();
    return apiRequest('/api/hr/shift-records', { method: 'POST', body: input, token });
  }
  async updateShiftRecord(id: string, patch: UpdateShiftRecordInput): Promise<ShiftRecord> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/shift-records/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteShiftRecord(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/hr/shift-records/${id}`, { method: 'DELETE', token });
  }

  async listRateFactors(): Promise<RateFactor[]> {
    const token = await this.getToken();
    return apiRequest('/api/hr/rate-factors', { token });
  }
  async createRateFactor(input: CreateRateFactorInput): Promise<RateFactor> {
    const token = await this.getToken();
    return apiRequest('/api/hr/rate-factors', { method: 'POST', body: input, token });
  }
  async updateRateFactor(id: string, patch: UpdateRateFactorInput): Promise<RateFactor> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/rate-factors/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteRateFactor(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/hr/rate-factors/${id}`, { method: 'DELETE', token });
  }

  async listCalendarDays(filters: ListCalendarDaysFilters): Promise<CalendarDay[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.year) params.set('year', String(filters.year));
    if (filters.month) params.set('month', String(filters.month));
    if (filters.dayType) params.set('dayType', filters.dayType);
    return apiRequest(`/api/hr/calendar-days?${params.toString()}`, { token });
  }
  async createCalendarDay(input: CreateCalendarDayInput): Promise<CalendarDay> {
    const token = await this.getToken();
    return apiRequest('/api/hr/calendar-days', { method: 'POST', body: input, token });
  }
  async createCalendarDaysBulk(inputs: CreateCalendarDayInput[]): Promise<{ inserted: number; skipped: number }> {
    const token = await this.getToken();
    return apiRequest('/api/hr/calendar-days/bulk', { method: 'POST', body: { rows: inputs }, token });
  }
  async updateCalendarDay(id: string, patch: UpdateCalendarDayInput): Promise<CalendarDay> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/calendar-days/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteCalendarDay(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/hr/calendar-days/${id}`, { method: 'DELETE', token });
  }
  async deleteCalendarYear(year: number): Promise<number> {
    const token = await this.getToken();
    const res = await apiRequest<{ deleted: number }>(`/api/hr/calendar-days/year/${year}`, { method: 'DELETE', token });
    return res.deleted;
  }

  async listStaging(filters: ListStagingFilters): Promise<CalendarStagingRow[]> {
    const token = await this.getToken();
    const params = new URLSearchParams();
    if (filters.importBatchId) params.set('importBatchId', filters.importBatchId);
    if (filters.sourceYear) params.set('sourceYear', String(filters.sourceYear));
    if (filters.sourceMonth) params.set('sourceMonth', String(filters.sourceMonth));
    if (filters.status) params.set('status', filters.status);
    return apiRequest(`/api/hr/calendar-staging?${params.toString()}`, { token });
  }
  async createStagingBatch(input: CreateStagingBatchInput): Promise<{ importBatchId: string; rows: CalendarStagingRow[] }> {
    const token = await this.getToken();
    return apiRequest('/api/hr/calendar-staging/batches', { method: 'POST', body: input, token });
  }
  async updateStagingRow(id: string, patch: UpdateStagingRowInput): Promise<CalendarStagingRow> {
    const token = await this.getToken();
    return apiRequest(`/api/hr/calendar-staging/${id}`, { method: 'PATCH', body: patch, token });
  }
  async deleteStagingRow(id: string): Promise<void> {
    const token = await this.getToken();
    await apiRequest(`/api/hr/calendar-staging/${id}`, { method: 'DELETE', token });
  }
  async confirmMonth(sourceYear: number, sourceMonth: number): Promise<number> {
    const token = await this.getToken();
    const res = await apiRequest<{ confirmed: number }>('/api/hr/calendar-staging/confirm', { method: 'POST', body: { sourceYear, sourceMonth }, token });
    return res.confirmed;
  }

  async getSummary(dateFrom: string, dateTo: string, employeeId?: string): Promise<SummaryRow[]> {
    const token = await this.getToken();
    const params = new URLSearchParams({ dateFrom, dateTo });
    if (employeeId) params.set('employeeId', employeeId);
    return apiRequest(`/api/hr/summary?${params.toString()}`, { token });
  }
}
